import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Sidebar.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faCalendarAlt, faUsers, faUserFriends, faBoxes, faUserDoctor, faCog, faSignOut, faComment } from '@fortawesome/free-solid-svg-icons';

const Sidebar = ({ isSidebarVisible }) => {
    const [user, setUser] = useState(null);
    const [profileImage, setProfileImage] = useState(null);
    const [profileName, setProfileName] = useState('');
    const [initials, setInitials] = useState('');
    const location = useLocation();
    const [showPatientsMenu, setShowPatientsMenu] = useState(false);
    const [showUserManagementMenu, setShowUserManagementMenu] = useState(false);
    const [showDashboardMenu, setShowDashboardMenu] = useState(false);
    const [showInventoryMenu, setShowInventoryMenu] = useState(false); // State for Inventory submenu
    const [userType, setUserType] = useState(null); // 'internal' or 'external'
    const [profileData, setProfileData] = useState(null);
    const [patientData, setPatientData] = useState(null);

    useEffect(() => {
        const fetchProfileAndPatient = async () => {
            try {
                // 1. Fetch user profile
                const profileResponse = await fetch('https://api.hgcdental.clinic/api/profile', {
                    credentials: 'include'
                });
                const profileData = await profileResponse.json();
                setProfileData(profileData);
                setInitials(profileData.initials);
                setProfileName(`${profileData.firstName} ${profileData.lastName}`);
    
                if (profileData.profileImage) {
                    const profileImageUrl = profileData.profileImage.startsWith('https')
                        ? profileData.profileImage
                        : `https://api.hgcdental.clinic/uploads/${profileData.profileImage}`; // Adjust path if necessary
                    setProfileImage(profileImageUrl);
                }
    
                // 2. Fetch patient data if patientID exists
                if (profileData.patientID) {
                    const patientResponse = await fetch(`https://api.hgcdental.clinic/api/patients`);
                    const patientsData = await patientResponse.json();
    
                    if (Array.isArray(patientsData)) {
                        const matchedPatient = patientsData.find(patient => patient.patientID === profileData.patientID);
                        if (matchedPatient) {
                            setPatientData(matchedPatient);
                        }
                    }
                }
    
                // 3. Check user type (internal/external)
                const [externalRes, internalRes] = await Promise.all([
                    fetch('https://api.hgcdental.clinic/api/externalusers'),
                    fetch('https://api.hgcdental.clinic/api/internalusers'),
                ]);
    
                const externalUsers = await externalRes.json();
                const internalUsers = await internalRes.json();
    
                const isExternal = externalUsers.some(u => u.email === profileData.email);
                const isInternal = internalUsers.some(u => u.email === profileData.email);
    
                if (isExternal) setUserType('external');
                else if (isInternal) setUserType('internal');
                else setUserType(null);
    
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };
    
        fetchProfileAndPatient();
    }, []);
    
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/profile', {
                    credentials: 'include' // Important for sessions
                });
                const data = await response.json();
                setProfileData(data);
            } catch (error) {
                console.error('Error fetching profile:', error);
            }
        };

        fetchProfile();
    }, []);

    const toggleMenu = (menuName) => {
        if (menuName === 'showDashboardMenu') {
            setShowDashboardMenu(!showDashboardMenu);
        } else if (menuName === 'showPatientsMenu') {
            setShowPatientsMenu(!showPatientsMenu);
        } else if (menuName === 'showUserManagementMenu') {
            setShowUserManagementMenu(!showUserManagementMenu);
        } else if (menuName === 'showInventoryMenu') {
            setShowInventoryMenu(!showInventoryMenu); // Toggle Inventory submenu
        }
    };

    const isActive = (path) => location.pathname === path;
    const isSubmenuActive = (paths) => paths.includes(location.pathname);

    useEffect(() => {
        setShowDashboardMenu(isSubmenuActive(['/dashboard', '/reports']));
        setShowPatientsMenu(isSubmenuActive(['/patients', '/treatment']));
        setShowUserManagementMenu(isSubmenuActive(['/external-users', '/internal-users']));
        setShowInventoryMenu(isSubmenuActive(['/inventory', '/inventory-items', '/inventory-categories'])); // Handle Inventory submenu active state
    }, [location.pathname]);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                console.log("Fetching profile...");
                const response = await fetch('https://api.hgcdental.clinic/api/profile', {
                    credentials: 'include',
                });
    
                if (!response.ok) throw new Error('Failed to fetch profile.');
    
                const profile = await response.json();
                console.log("Profile data received:", profile);
    
                setInitials(profile.initials);
                setProfileName(`${profile.firstName} ${profile.lastName}`);
    
                if (profile.profileImage) {
                    const profileImageUrl = profile.profileImage.startsWith('https')
                        ? profile.profileImage
                        : `https://mongodb://mongo:RdyEfHGmhaNXHBosospsLcnMsJFAwCSK@centerbeam.proxy.rlwy.net:13440/${profile.profileImage}`;
    
                    console.log("Profile image URL:", profileImageUrl);
                    setProfileImage(profileImageUrl);
                }
    
                console.log("Fetching external and internal users...");
                const [externalRes, internalRes] = await Promise.all([
                    fetch('https://api.hgcdental.clinic/api/externalusers'),
                    fetch('https://api.hgcdental.clinic/api/internalusers'),
                ]);
    
                if (!externalRes.ok || !internalRes.ok) throw new Error('Failed to fetch user lists.');
    
                const externalUsers = await externalRes.json();
                const internalUsers = await internalRes.json();
    
                console.log("External users:", externalUsers);
                console.log("Internal users:", internalUsers);
    
                const isExternal = externalUsers.some(u => u.email === profile.email);
                const isInternal = internalUsers.some(u => u.email === profile.email);
    
                console.log("User type check - isExternal:", isExternal, "isInternal:", isInternal);
    
                if (isExternal) setUserType('external');
                else if (isInternal) setUserType('internal');
                else setUserType(null); // fallback if not found
    
            } catch (error) {
                console.error('Error fetching profile or user type:', error);
            }
        };
    
        fetchProfile();
    }, []);
    
    return (
        <div className={`sidebar ${isSidebarVisible ? 'open' : 'closed'}`}>
            {/* Profile Section */}
            <Link to="/my-profile">
                <div className="profile" style={{ cursor: 'pointer' }}>
                    {profileImage ? (
                        <img src={profileImage} alt="Profile" className="profile-image" />
                    ) : (
                        <div className="profile-initials">{initials}</div>
                    )}
                    <p className="profile-name">{profileName}</p>
                </div>
            </Link>

            {userType === 'internal' && (
                <>
                    <button
                        className={`sidebar-button ${!isSubmenuActive(['/dashboard', '/reports']) && isActive('/dashboard') ? 'active' : ''}`}
                        onClick={() => toggleMenu('showDashboardMenu')}
                    >
                        <FontAwesomeIcon icon={faHome} className="icon-lightblue" />
                        <span>Dashboard</span>
                        <span className="arrow">{showDashboardMenu ? '▼' : '▶'}</span>
                    </button>
                    {showDashboardMenu && (
                        <div className="submenu">
                            <Link to="/dashboard">
                                <button className={`submenu-button ${isActive('/dashboard') ? 'active' : ''}`}>Dashboard</button>
                            </Link>
                            <Link to="/reports">
                                <button className={`submenu-button ${isActive('/reports') ? 'active' : ''}`}>Reports</button>
                            </Link>
                        </div>
                    )}
                </>
            )}

            {userType === 'internal' && (
                <Link to="/appointments">
                    <button className={`sidebar-button ${isActive('/appointments') ? 'active' : ''}`}>
                        <FontAwesomeIcon icon={faCalendarAlt} className={`icon-red ${isActive('/appointments') ? 'icon-white' : ''}`} />
                        <span>Appointments</span>
                    </button>
                </Link>
            )}

            {userType === 'external' && (
                <Link to="/appointments-users-calendar">
                    <button className={`sidebar-button ${isActive('/appointments-users-calendar') ? 'active' : ''}`}>
                        <FontAwesomeIcon icon={faCalendarAlt} className={`icon-red ${isActive('/appointments-users-calendar') ? 'icon-white' : ''}`} />
                        <span>Appointments</span>
                    </button>
                </Link>
            )}

            {userType === 'external' && patientData && (
                <Link to={`/my-patient-profile/${patientData._id}`}> {/* Use _id instead of patientID */}
                    <button
                        className={`sidebar-button ${location.pathname === `/my-patient-profile/${patientData._id}` ? 'active' : ''}`}
                    >
                        <FontAwesomeIcon icon={faUsers} className="icon-darkblue" />
                        <span>Patient Profile</span>
                    </button>
                </Link>
            )}

            {userType && (
                <Link to="/chat">
                    <button className={`sidebar-button ${isActive('/chat') ? 'active' : ''}`}>
                        <FontAwesomeIcon icon={faComment} className={`icon-green ${isActive('/chat') ? 'icon-white' : ''}`} />
                        <span>Messages</span>
                    </button>
                </Link>
            )}

            {userType === 'internal' && (
                <>
                    <button
                        className={`sidebar-button ${!isSubmenuActive(['/patients', '/treatment']) && isActive('/patients') ? 'active' : ''}`}
                        onClick={() => toggleMenu('showPatientsMenu')}
                    >
                        <FontAwesomeIcon icon={faUsers} className="icon-darkblue" />
                        <span>Patients</span>
                        <span className="arrow">{showPatientsMenu ? '▼' : '▶'}</span>
                    </button>
                    {showPatientsMenu && (
                        <div className="submenu">
                            <Link to="/patients">
                                <button className={`submenu-button ${isActive('/patients') ? 'active' : ''}`}>Patients</button>
                            </Link>
                            <Link to="/treatment">
                                <button className={`submenu-button ${isActive('/treatment') ? 'active' : ''}`}>Treatments</button>
                            </Link>
                        </div>
                    )}
                </>
            )}

            {userType === 'internal' && (
                <Link to="/dentists">
                    <button className={`sidebar-button ${isActive('/dentists') ? 'active' : ''}`}>
                        <FontAwesomeIcon icon={faUserDoctor} className={`icon-green ${isActive('/dentists') ? 'icon-white' : ''}`} />
                        <span>Dentists</span>
                    </button>
                </Link>
            )}

            {userType === 'internal' && (
                <>
                    <button
                        className={`sidebar-button ${!isSubmenuActive(['/inventory', '/suppliers', '/inventory-categories']) && isActive('/inventory') ? 'active' : ''}`}
                        onClick={() => toggleMenu('showInventoryMenu')}
                    >
                        <FontAwesomeIcon icon={faBoxes} className="icon-brown" />
                        <span>Inventory</span>
                        <span className="arrow">{showInventoryMenu ? '▼' : '▶'}</span>
                    </button>
                    {showInventoryMenu && (
                        <div className="submenu">
                            <Link to="/inventory">
                                <button className={`submenu-button ${isActive('/inventory') ? 'active' : ''}`}>Items</button>
                            </Link>
                            <Link to="/suppliers">
                                <button className={`submenu-button ${isActive('/suppliers') ? 'active' : ''}`}>Suppliers</button>
                            </Link>
                            <Link to="/inventory-categories">
                                <button className={`submenu-button ${isActive('/inventory-categories') ? 'active' : ''}`}>Categories</button>
                            </Link>
                        </div>
                    )}
                </>
            )}

            {userType === 'internal' && (
                <>
                    <button
                        className={`sidebar-button ${!isSubmenuActive(['/external-users', '/internal-users']) && isActive('/external-users') ? 'active' : ''}`}
                        onClick={() => toggleMenu('showUserManagementMenu')}
                    >
                        <FontAwesomeIcon icon={faUserFriends} className="icon-lightblue" />
                        <span>Users</span>
                        <span className="arrow">{showUserManagementMenu ? '▼' : '▶'}</span>
                    </button>

                    {showUserManagementMenu && (
                        <div className="submenu">
                            <Link to="/external-users">
                                <button className={`submenu-button ${isActive('/external-users') ? 'active' : ''}`}>External Users</button>
                            </Link>
                            <Link to="/internal-users">
                                <button className={`submenu-button ${isActive('/internal-users') ? 'active' : ''}`}>Internal Users</button>
                            </Link>
                        </div>
                    )}
                </>
            )}

            <div className="sidebar-divider" />

            <Link to="/">
                <button className={`sidebar-button ${isActive('/') ? 'active' : ''}`}>
                    <FontAwesomeIcon icon={faSignOut} className="icon" style={{ color: '757575' }} />
                    <span>Log Out</span>
                </button>
            </Link>
        </div>
    );
};

export default Sidebar;